import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * One shared clock for the tournament tiles' theme animations — one THEME per
 * window, in rotation.
 *
 * What heats a phone is frame PRODUCTION: while anything animates the WebView
 * draws at the display refresh rate, and the cost is shared, not added per
 * animation (docs/investigations/20260819-game-client-overheating.md,
 * sections 1, 4, 8b-8c). So the lever is how long something is moving, and
 * the clock bounds it: each window plays ONE theme — every on-screen tile of
 * it, together — at that theme's own natural speed, for as long as its show
 * runs (THEME_SHOW_MS), and then nothing animates for a fixed GAP_MS before
 * the next theme's window. A long show buys itself no less rest. The shows END
 * (section 8c: "fire the effect on a JS timer, let the animation END"), so
 * between windows no animation object is alive and no layer is held.
 *
 * Only themes with a tile on screen take a turn, so a window is never spent
 * animating what nobody can see; with none on screen, the clock checks again
 * a gap later. Timers, not requestAnimationFrame: a pending rAF alone forces
 * full-rate frames on this WebView (section 8d). The clock runs only while a
 * tile is listening, skips windows while the page is hidden, and closes the
 * open one the moment the page is hidden.
 */

// Rest after every show, before the next theme's window opens.
export const GAP_MS = 60_000;
const FIRST_SHOW_DELAY_MS = 1_000;
// The window closes this long after the show's last element ends, so a show
// is never cut off by its own window closing.
const CLOSE_MARGIN_MS = 300;

/**
 * Each theme's show at its natural speed: one cycle, plus its latest staggered
 * start. Must match the animations in GameTile.module.scss.
 */
export const THEME_SHOW_MS = {
  midnight: 7_000, // shooting star 7s
  highnoon: 12_000, // sun pulse 12s
  sunrise: 18_000, // dim + sun 18s
  freezeout: 18_860, // frost crack + impact 14s, last crack starts at 4.86s
  satellite: 6_000, // wave 3.6s, last wave starts at 2.4s
  bounty: 11_800, // bullet 10s, last bullet starts at 1.8s
  tumble: 14_000 // tumbleweed 14s
} as const;

export type ShowTheme = keyof typeof THEME_SHOW_MS;
// The order themes take their turns in.
const ROTATION = Object.keys(THEME_SHOW_MS) as ShowTheme[];

const isShowTheme = (theme: string | null | undefined): theme is ShowTheme =>
  !!theme && theme in THEME_SHOW_MS;

export interface TileShow {
  // Counts windows opened, so a tile can tell a new one from the one it is in.
  showId: number;
  // The theme whose window is open, or null between windows.
  theme: ShowTheme | null;
}

interface TileEntry {
  theme: ShowTheme;
  inView: boolean;
}

let state: TileShow = { showId: 0, theme: null };
const listeners = new Set<() => void>();
const tiles = new Map<object, TileEntry>();
// The tiles the open window is for: those of its theme on screen when it
// opened. One that mounts, scrolls in or changes theme mid-window waits for
// its theme's next turn instead of starting a show partway through.
let cast = new Set<object>();
let lastTheme: ShowTheme | null = null;
// The one pending timer: the next window opening, or the open one closing.
let timer: ReturnType<typeof setTimeout> | undefined;

const publish = (next: TileShow) => {
  state = next;
  listeners.forEach((listener) => listener());
};

/**
 * The next theme in rotation, after the last one played, with a tile on screen.
 * The first turn after the clock starts — a page load, or the lobby opening
 * again — is a random pick among the themes on screen, each as likely as the
 * next, so it is not the same theme every time; from there the themes take
 * their turns in order.
 */
const nextTheme = (): ShowTheme | null => {
  const onScreen = new Set<ShowTheme>();
  tiles.forEach((entry) => {
    if (entry.inView) onScreen.add(entry.theme);
  });
  const candidates = ROTATION.filter((theme) => onScreen.has(theme));
  if (candidates.length === 0) return null;
  if (!lastTheme) return candidates[Math.floor(Math.random() * candidates.length)];
  const after = ROTATION.indexOf(lastTheme);
  return candidates.find((theme) => ROTATION.indexOf(theme) > after) ?? candidates[0];
};

const scheduleOpen = (delay: number) => {
  timer = setTimeout(openWindow, delay);
};

// Open the next theme's window, close it when its show ends, then rest a gap.
// Nothing to show (page hidden, no themed tile on screen): rest a gap anyway.
function openWindow() {
  const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
  const theme = hidden ? null : nextTheme();
  if (!theme) {
    scheduleOpen(GAP_MS);
    return;
  }
  lastTheme = theme;
  cast = new Set(
    Array.from(tiles)
      .filter(([, entry]) => entry.theme === theme && entry.inView)
      .map(([key]) => key)
  );
  publish({ showId: state.showId + 1, theme });
  timer = setTimeout(closeWindow, THEME_SHOW_MS[theme] + CLOSE_MARGIN_MS);
}

function closeWindow() {
  cast = new Set();
  publish({ ...state, theme: null });
  scheduleOpen(GAP_MS);
}

// Hidden mid-show: close the window now rather than when the show would have
// ended, so a page coming back never picks a show up partway; then rest a gap.
const onVisibilityChange = () => {
  if (document.visibilityState !== 'hidden' || !state.theme) return;
  clearTimeout(timer);
  closeWindow();
};

const start = () => {
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  scheduleOpen(FIRST_SHOW_DELAY_MS);
};

const stop = () => {
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }
  clearTimeout(timer);
  timer = undefined;
  cast = new Set();
  state = { showId: state.showId, theme: null };
  lastTheme = null;
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  if (listeners.size === 1) start();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) stop();
  };
};

// The window open right now, whichever theme it is for.
export const getTileShow = (): TileShow => state;

/**
 * The shared show window, as one tile sees it: the id of the window open for
 * its own theme — if it was on screen with that theme when the window opened
 * (`cast`) and has stayed so — or null. A tile hands in its theme and whether
 * it can play (on screen, animations not reduced), so the clock only gives
 * turns to themes someone can see. One that changes theme or stops being able
 * to play mid-window leaves the cast, so coming back cannot restart the show
 * partway: it waits for its theme's next window. Every tile listens, but a window re-renders only the tiles
 * of its theme: the others' snapshot stays null throughout.
 */
export const useTileShow = (theme: string | null, canPlay: boolean): number | null => {
  const key = useRef({});
  useEffect(() => {
    const entry = key.current;
    if (isShowTheme(theme)) tiles.set(entry, { theme, inView: canPlay });
    else tiles.delete(entry);
    if (!canPlay || theme !== state.theme) cast.delete(entry);
    return () => {
      tiles.delete(entry);
    };
  }, [theme, canPlay]);
  const getOwnShow = useCallback(
    () =>
      canPlay && isShowTheme(theme) && state.theme === theme && cast.has(key.current)
        ? state.showId
        : null,
    [theme, canPlay]
  );
  return useSyncExternalStore(subscribe, getOwnShow);
};
